import { useState, useRef, useCallback, useEffect } from "react";
import { MoveHorizontal, ChevronLeft, ChevronRight } from "lucide-react";

const PAIRS = [
  {
    before:
      "https://customer-assets.emergentagent.com/job_swift-toilet-pro/artifacts/t03048mh_IMG_20240211_231721_302.jpg",
    after:
      "https://customer-assets.emergentagent.com/job_swift-toilet-pro/artifacts/y09k6fue_IMG_20240211_231751_174.jpg",
    title: "Marokkaans tegelwerk",
    location: "Klant in Amsterdam",
    description:
      "Oude witte tegels en versleten wastafel vervangen door warm betonlook met sierlijk Marokkaans accent.",
  },
  {
    before:
      "https://customer-assets.emergentagent.com/job_swift-toilet-pro/artifacts/l36fliu4_IMG_20210619_105511_284.jpg",
    after:
      "https://customer-assets.emergentagent.com/job_swift-toilet-pro/artifacts/trsg2nmi_20210528_222240.jpg",
    title: "Strak modern wit",
    location: "Klant in regio Amstelveen",
    description:
      "Gedateerde toiletruimte met losse staander en oude tegels getransformeerd tot een strakke, moderne uitstraling met hangtoilet en betonlook tegels.",
  },
  {
    before:
      "https://customer-assets.emergentagent.com/job_swift-toilet-pro/artifacts/p3xpw7au_IMG-20171003-WA0001.jpg",
    after:
      "https://customer-assets.emergentagent.com/job_swift-toilet-pro/artifacts/vgqaewqq_IMG_20210821_190801_083.webp",
    title: "Van mozaïek naar tijdloos",
    location: "Klant in Amsterdam",
    description:
      "Drukke blauwe mozaïek tegels en gedateerde afwerking vervangen door een rustige, tijdloze look waarin het toilet weer tot z'n recht komt.",
  },
  {
    before:
      "https://customer-assets.emergentagent.com/job_swift-toilet-pro/artifacts/ad95siiy_IMG-20201110-WA0044.jpg",
    after:
      "https://customer-assets.emergentagent.com/job_swift-toilet-pro/artifacts/bvel68dm_IMG-20201120-WA0057.jpg",
    title: "Marmer & zwart accent",
    location: "Klant in Amsterdam",
    description:
      "Oud staand toilet met zwarte bril vervangen door een hangtoilet, zwart wastafelmeubel en wand-tot-wand marmerlook tegelwerk.",
  },
];

function BeforeAfterSlider({ before, after, ariaLabel, resetKey }) {
  const [position, setPosition] = useState(50);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);

  // Reset slider position when pair changes
  useEffect(() => {
    setPosition(50);
  }, [resetKey]);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPosition(pct);
  }, []);

  const handlePointerDown = (e) => {
    setDragging(true);
    updateFromClientX(e.clientX);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const handlePointerMove = (e) => {
    if (!dragging) return;
    updateFromClientX(e.clientX);
  };
  const handlePointerUp = (e) => {
    setDragging(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowLeft") setPosition((p) => Math.max(0, p - 4));
    if (e.key === "ArrowRight") setPosition((p) => Math.min(100, p + 4));
    if (e.key === "Home") setPosition(0);
    if (e.key === "End") setPosition(100);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => updateFromClientX(e.clientX);
    const onUp = () => setDragging(false);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [dragging, updateFromClientX]);

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[4/5] sm:aspect-square overflow-hidden rounded-3xl border-4 border-white shadow-2xl bg-slate-200 select-none touch-none"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      role="slider"
      tabIndex={0}
      aria-label={ariaLabel || "Voor en na vergelijking"}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(position)}
      onKeyDown={handleKeyDown}
      data-testid="before-after-slider"
    >
      <img
        src={after}
        alt="Na de renovatie"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        draggable={false}
      />
      <img
        src={before}
        alt="Voor de renovatie"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
        draggable={false}
      />

      <span className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-slate-900/80 text-white text-xs font-bold uppercase tracking-widest backdrop-blur-sm">
        Voor
      </span>
      <span className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-orange-500 text-white text-xs font-bold uppercase tracking-widest">
        Na
      </span>

      <div
        className="absolute top-0 bottom-0 w-[3px] bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)] pointer-events-none"
        style={{ left: `calc(${position}% - 1.5px)` }}
      >
        <button
          type="button"
          aria-label="Sleep om voor en na te vergelijken"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white text-blue-700 shadow-xl flex items-center justify-center pointer-events-auto cursor-ew-resize border-2 border-blue-700 hover:scale-110 transition-transform"
          data-testid="slider-handle"
        >
          <MoveHorizontal className="w-5 h-5" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

export default function BeforeAfter() {
  const [index, setIndex] = useState(0);
  const total = PAIRS.length;
  const pair = PAIRS[index];

  const next = () => setIndex((i) => (i + 1) % total);
  const prev = () => setIndex((i) => (i - 1 + total) % total);

  return (
    <section
      id="voor-na"
      className="bg-white py-20 lg:py-28"
      data-testid="before-after-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Centered intro */}
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-orange-500">
            Voor &amp; na
          </p>
          <h2 className="font-display font-bold mt-3 text-4xl sm:text-5xl tracking-tight text-slate-900 leading-[1.05]">
            Sleep en zie het{" "}
            <span className="text-blue-700">verschil</span>.
          </h2>
          <p
            className="mt-5 text-lg text-slate-600 mx-auto"
            data-testid="pair-description"
          >
            {pair.description}
          </p>

          <div className="mt-8 grid grid-cols-3 gap-4 max-w-md mx-auto">
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 text-center">
              <div className="font-display font-bold text-2xl text-blue-700">3</div>
              <div className="text-xs text-slate-600 mt-1 leading-tight">dagen werk</div>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 text-center">
              <div className="font-display font-bold text-2xl text-orange-500">100%</div>
              <div className="text-xs text-slate-600 mt-1 leading-tight">vakwerk</div>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 text-center">
              <div className="font-display font-bold text-2xl text-blue-700">2 jr</div>
              <div className="text-xs text-slate-600 mt-1 leading-tight">garantie</div>
            </div>
          </div>
        </div>

        {/* Centered slider */}
        <div className="mt-12 max-w-3xl mx-auto">
          <BeforeAfterSlider
            key={index}
            resetKey={index}
            before={pair.before}
            after={pair.after}
            ariaLabel={`Voor en na: ${pair.title}`}
          />
          <div className="mt-4 flex items-center justify-between text-sm">
            <span
              className="font-display font-semibold text-slate-900"
              data-testid="pair-title"
            >
              {pair.title}
            </span>
            <span className="text-slate-500" data-testid="pair-location">
              {pair.location}
            </span>
          </div>
        </div>

        {/* Centered carousel controls */}
        <div className="mt-8 flex items-center justify-center gap-4" data-testid="carousel-controls">
          <button
            type="button"
            onClick={prev}
            aria-label="Vorige voor en na"
            className="w-12 h-12 rounded-full border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white transition-colors flex items-center justify-center"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-2">
            {PAIRS.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Ga naar voor en na ${i + 1}`}
                className={`h-2 rounded-full transition-all ${
                  i === index
                    ? "w-8 bg-orange-500"
                    : "w-2 bg-slate-300 hover:bg-slate-400"
                }`}
                data-testid={`carousel-dot-${i}`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={next}
            aria-label="Volgende voor en na"
            className="w-12 h-12 rounded-full bg-blue-700 text-white hover:bg-blue-800 transition-colors flex items-center justify-center"
            data-testid="carousel-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <span className="font-display font-bold text-slate-400 text-sm tabular-nums ml-2" data-testid="carousel-counter">
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
        </div>

        <p className="mt-4 text-sm text-slate-500 italic text-center" data-testid="slider-hint">
          Tip: sleep het schuifje of gebruik ← → toetsen.
        </p>
      </div>
    </section>
  );
}
